<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { Button } from '../button';

import type { WorkflowTourAdapter, WorkflowTourDefinition } from './types';
import {
  WORKFLOW_TOUR_DRAG_START,
  workflowTourProgress,
  workflowTourTimelineStart,
  workflowTourTimelineUpdate,
  type WorkflowTourEffect,
  type WorkflowTourEvent,
  type WorkflowTourTimeline,
} from './workflow-tour-timeline';
const props = defineProps<{
  root: HTMLElement | null;
  disabled: boolean;
  definition: WorkflowTourDefinition;
  adapter: WorkflowTourAdapter;
  autoStart: boolean;
  returnLabel: string | null;
}>();
const emit = defineEmits<{ return: []; closed: [] }>();
const steps = computed(() => props.definition.steps);
const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
const context = computed(() => ({ steps: steps.value, reducedMotion: reducedMotion.value }));
// Step sequencing lives in workflow-tour-timeline.ts; this component only drives the DOM. null while inactive.
const timeline = ref<WorkflowTourTimeline | null>(null);
const active = computed(() => timeline.value !== null);
const step = computed(() => timeline.value?.step ?? 0);
const playing = computed(() => timeline.value?.playing ?? false);
const phase = computed(() => timeline.value?.phase.kind ?? 'preparing');
const preparing = computed(() => phase.value === 'preparing');
const unavailable = computed(() => phase.value === 'unavailable');
const finished = computed(() => phase.value === 'finished');
const applied = computed(() => {
  const current = timeline.value?.phase;
  return current?.kind === 'finished' || (current?.kind === 'running' && current.applied);
});
const progress = computed(() => (timeline.value ? workflowTourProgress(context.value, timeline.value) : 0));
const coach = ref<HTMLElement | null>(null);
const ghost = ref<HTMLElement | null>(null);
const maskID = useId();
const current = computed(() => steps.value[step.value]!);
type Rect = { x: number; y: number; width: number; height: number };
const highlights = ref<Rect[]>([]);
const viewport = ref({ width: 0, height: 0 });
const cursor = ref({ x: 0, y: 0 });
const ghostWidth = ref(180);
let positioningElapsed = 0;
type ScrollMove = { element: HTMLElement; left: number; top: number; dx: number; dy: number };
let positioning: ScrollMove[] = [];
let dragScroll: ScrollMove[] = [];
let resultScroll: ScrollMove[] = [];
let resultElapsed = 0;
let source: HTMLElement | null = null;
let target: HTMLElement | null = null;
let from = { x: 0, y: 0 };
let frame = 0;
let run = 0;

let lastTime = 0;
let priorFocus: HTMLElement | null = null;
let spacer: HTMLElement | null = null;
let anchorStyles: Array<{ element: HTMLElement; value: string }> = [];
let scrollPositions: Array<{ element: HTMLElement; top: number; left: number }> = [];

function find(name: string) {
  return props.root?.querySelector<HTMLElement>(name) ?? null;
}
function rect(element: HTMLElement): Rect {
  const bounds = element.getBoundingClientRect();
  return { x: bounds.left - 6, y: bounds.top - 6, width: bounds.width + 12, height: bounds.height + 12 };
}
function center(element: HTMLElement) {
  const bounds = element.getBoundingClientRect();
  return { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 };
}
function updateGeometry() {
  viewport.value = { width: window.innerWidth, height: window.innerHeight };
  const elements = [source, target].filter((el): el is HTMLElement => !!el && el.isConnected);
  highlights.value = elements.map(rect).filter((r) => r.y + r.height > 0 && r.y < window.innerHeight);
  const focus = target ?? source;
  if (!focus) return;
  const destination = center(focus);

  const eased = current.value.target ? dragFraction(progress.value) : 1;
  cursor.value = { x: from.x + (destination.x - from.x) * eased, y: from.y + (destination.y - from.y) * eased };
}

function ease(value: number) {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}
function dragFraction(stepProgress: number) {
  return ease((stepProgress - WORKFLOW_TOUR_DRAG_START) / 0.5);
}

// Scroll and pointer movement share one clock. Reserve space for the fixed coach.
function planScroll(element: HTMLElement, companion: HTMLElement | null): ScrollMove[] {
  const moves: ScrollMove[] = [];
  const box = element.getBoundingClientRect();
  const other = companion?.getBoundingClientRect();
  const bottom = (coach.value?.getBoundingClientRect().top || window.innerHeight - 260) - 24;
  let top = box.top;
  let end = box.bottom;
  if (other && Math.max(end, other.bottom) - Math.min(top, other.top) < bottom - 48) {
    top = Math.min(top, other.top);
    end = Math.max(end, other.bottom);
  }
  let dy = (top + end) / 2 - (48 + bottom) / 2;
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const documentScroll = parent === document.scrollingElement;
    const style = getComputedStyle(parent);
    if (!documentScroll && !/(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX)) continue;
    const maxY = Math.max(0, parent.scrollHeight - parent.clientHeight);
    const deltaY = Math.max(-parent.scrollTop, Math.min(dy, maxY - parent.scrollTop));
    const bounds = documentScroll ? { left: 0, right: window.innerWidth } : parent.getBoundingClientRect();
    const dx =
      box.left < bounds.left + 12
        ? box.left - bounds.left - 12
        : box.right > bounds.right - 12
          ? box.right - bounds.right + 12
          : 0;
    moves.push({
      element: parent,
      left: parent.scrollLeft,
      top: parent.scrollTop,
      dx: Math.max(-parent.scrollLeft, Math.min(dx, parent.scrollWidth - parent.clientWidth - parent.scrollLeft)),
      dy: deltaY,
    });
    dy -= deltaY;
  }
  return moves;
}
function scrollAt(moves: ScrollMove[], fraction: number) {
  for (const move of moves)
    move.element.scrollTo({
      left: move.left + move.dx * fraction,
      top: move.top + move.dy * fraction,
      behavior: 'instant',
    });
}
function dispatch(event: WorkflowTourEvent) {
  if (!timeline.value) return Promise.resolve();
  const transition = workflowTourTimelineUpdate(context.value, timeline.value, event);
  timeline.value = transition.timeline;
  return effectsRun(transition.effects);
}
async function effectsRun(effects: WorkflowTourEffect[]) {
  for (const effect of effects) {
    if (effect.kind === 'perform') stepPerform(effect.stepID);
    else await stepPrepare(effect.stepID);
  }
}
function positioned() {
  from = source ? center(source) : { x: 0, y: 0 };
  dragScroll = target ? planScroll(target, null) : [];
  if (reducedMotion.value) scrollAt(dragScroll, 1);
  void dispatch({ kind: 'positioned' });
  updateGeometry();
}
function stepPerform(stepID: string) {
  const token = run;
  props.adapter.perform(stepID);
  // A selected label can resize a table column. Reframe the rendered result so
  // long options remain visible on narrow screens after the simulated action.
  void nextTick(() => {
    const focus = target ?? source;
    if (!active.value || token !== run || !focus?.isConnected) return;
    resultScroll = planScroll(focus, null);
    resultElapsed = 0;
    if (reducedMotion.value) scrollAt(resultScroll, 1);
  });
}
async function stepPrepare(stepID: string) {
  if (!active.value) return;
  const token = ++run;
  positioningElapsed = 0;
  highlights.value = [];
  source = null;
  target = null;
  positioning = [];
  resultScroll = [];
  props.adapter.prepare(stepID);
  await nextTick();
  if (!active.value || token !== run) return;
  source = find(current.value.source);
  target = current.value.target ? find(current.value.target) : null;
  ghost.value?.replaceChildren();
  if (!source || (current.value.target && !target)) {
    void dispatch({ kind: 'targetsMissing' });
    return;
  }
  if (target && ghost.value) {
    const clone = source.cloneNode(true) as HTMLElement;
    for (const el of [clone, ...clone.querySelectorAll<HTMLElement>('*')]) {
      el.removeAttribute('id');
      el.removeAttribute('aria-describedby');
      el.removeAttribute('tabindex');
      for (const attribute of [...el.attributes])
        if (attribute.name.startsWith('data-')) el.removeAttribute(attribute.name);
    }
    ghostWidth.value = Math.min(source.getBoundingClientRect().width, 260);
    clone.style.width = '100%';
    clone.style.margin = '0';
    clone.style.whiteSpace = 'normal';
    ghost.value.append(clone);
  }
  positioning = planScroll(source, target);
  if (reducedMotion.value || positioning.every((move) => Math.abs(move.dx) < 1 && Math.abs(move.dy) < 1)) {
    scrollAt(positioning, 1);
    positioned();
  }
  lastTime = performance.now();
  updateGeometry();
}

function tick(now: number) {
  if (!active.value) return;
  const delta = Math.min(now - lastTime, 100);
  lastTime = now;
  if (preparing.value && source) {
    positioningElapsed += delta;
    scrollAt(positioning, ease(positioningElapsed / 550));
    if (positioningElapsed >= 550) positioned();
  } else if (phase.value === 'running' && playing.value && !reducedMotion.value) {
    if (!applied.value) scrollAt(dragScroll, dragFraction(progress.value));
    else if (resultScroll.length && resultElapsed < 550) {
      resultElapsed += delta;
      scrollAt(resultScroll, ease(resultElapsed / 550));
    }
  }
  void dispatch({ kind: 'tick', delta });
  updateGeometry();
  if (active.value) frame = requestAnimationFrame(tick);
}

async function begin() {
  if (active.value || props.disabled || !props.root || !steps.value.length) return;
  priorFocus = document.activeElement as HTMLElement | null;
  scrollPositions = [];
  for (let element: HTMLElement | null = props.root; element; element = element.parentElement) {
    scrollPositions.push({ element, top: element.scrollTop, left: element.scrollLeft });
    anchorStyles.push({ element, value: element.style.overflowAnchor });
    element.style.overflowAnchor = 'none';
  }
  for (const element of props.root.querySelectorAll<HTMLElement>('*')) {
    if (element.scrollWidth > element.clientWidth)
      scrollPositions.push({ element, top: element.scrollTop, left: element.scrollLeft });
  }
  props.adapter.start();
  // Allow even the last control to scroll above the coach on short screens.
  spacer = document.createElement('div');
  spacer.style.height = 'calc(45vh + 64px)';
  spacer.setAttribute('aria-hidden', 'true');
  props.root.after(spacer);
  const start = workflowTourTimelineStart(context.value);
  timeline.value = start.timeline;
  await nextTick();
  if (!active.value) return;
  coach.value?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  document.addEventListener('keydown', onKeydown, true);
  await effectsRun(start.effects);
  if (active.value) frame = requestAnimationFrame(tick);
}
function stop(returnRequested: boolean, notify: boolean) {
  if (!active.value) return;
  ++run;
  cancelAnimationFrame(frame);
  document.removeEventListener('keydown', onKeydown, true);
  timeline.value = null;
  ghost.value?.replaceChildren();
  props.adapter.finish();
  spacer?.remove();
  spacer = null;
  for (const { element, value } of anchorStyles) element.style.overflowAnchor = value;
  anchorStyles = [];
  void nextTick(() => {
    for (const { element, top, left } of scrollPositions) element.scrollTo({ top, left, behavior: 'instant' });
    if (priorFocus?.isConnected) priorFocus.focus({ preventScroll: true });
    if (notify) {
      if (returnRequested) emit('return');
      else emit('closed');
    }
  });
}
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    stop(false, true);
  }
  if (event.key !== 'Tab') return;
  const buttons = [...(coach.value?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
  const first = buttons[0];
  const last = buttons.at(-1);
  if (!coach.value?.contains(document.activeElement)) {
    event.preventDefault();
    first?.focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
}
let autoStarted = false;
watch(
  () => [props.autoStart, props.disabled, props.root],
  () => {
    if (props.autoStart && !props.disabled && props.root && !autoStarted) {
      autoStarted = true;
      void begin();
    }
  },
  { immediate: true, flush: 'post' },
);
onBeforeUnmount(() => stop(false, false));
</script>

<template>
  <Button type="button" variant="outline" size="sm" :disabled="disabled" @click="begin"
    ><i class="fa-light fa-circle-play mr-2" aria-hidden="true" />{{ definition.launchLabel }}</Button
  >
  <Teleport to="body">
    <div
      v-if="active"
      class="fixed inset-0 z-[10000]"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`${maskID}-title`"
      :aria-describedby="`${maskID}-description`"
      data-workflow-tour-overlay
      :data-tour-id="definition.id"
    >
      <svg class="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <mask :id="maskID" maskUnits="userSpaceOnUse" :x="0" :y="0" :width="viewport.width" :height="viewport.height">
            <rect width="100%" height="100%" fill="white" />
            <rect
              v-for="(r, i) in highlights"
              :key="i"
              :x="r.x"
              :y="r.y"
              :width="r.width"
              :height="r.height"
              rx="10"
              fill="black"
            />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="#0f172a" fill-opacity="0.64" :mask="`url(#${maskID})`" />
        <rect
          v-for="(r, i) in highlights"
          :key="i"
          :x="r.x"
          :y="r.y"
          :width="r.width"
          :height="r.height"
          rx="10"
          fill="none"
          :stroke="applied ? '#34d399' : '#c084fc'"
          stroke-width="3"
        />
      </svg>
      <div
        ref="ghost"
        class="pointer-events-none fixed origin-center shadow-xl"
        :style="{
          width: ghostWidth + 'px',
          left: cursor.x + 'px',
          top: cursor.y + 'px',
          transform: 'translate(-50%, -50%) rotate(-4deg)',
          opacity: !preparing && current.target && progress > 0.13 && progress < 0.74 && !reducedMotion ? 0.95 : 0,
        }"
        aria-hidden="true"
        inert
      />
      <div
        v-if="!reducedMotion && !preparing && !unavailable"
        class="pointer-events-none fixed text-purple-700 drop-shadow-lg"
        :style="{
          left: cursor.x + 'px',
          top: cursor.y + 'px',
          transform: progress > 0.15 && progress < 0.72 ? 'scale(.9)' : 'scale(1)',
        }"
        aria-hidden="true"
      >
        <svg width="34" height="40" viewBox="0 0 34 40" fill="currentColor">
          <path
            d="M9 22V6a3 3 0 0 1 6 0v11-2a3 3 0 0 1 6 0v3a3 3 0 0 1 6 0v3a3 3 0 0 1 6 0v9c0 5-4 9-9 9h-5c-4 0-7-2-9-5l-8-10c-2-3 2-6 5-3l2 1Z"
            stroke="white"
            stroke-width="2"
          />
        </svg>
      </div>
      <section
        ref="coach"
        class="fixed bottom-4 left-1/2 h-80 max-h-[45dvh] w-[calc(100vw_-_2rem)] max-w-md -translate-x-1/2 overflow-y-auto rounded-xl border border-purple-100 bg-white p-4 shadow-2xl sm:h-72"
        data-tour-coach
      >
        <div class="flex items-center justify-between gap-3">
          <span class="text-xs font-semibold text-purple-700">LIVE TOUR · {{ step + 1 }} / {{ steps.length }}</span
          ><button
            type="button"
            class="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
            @click="stop(false, true)"
          >
            Exit tour
          </button>
        </div>
        <div aria-live="polite" aria-atomic="true">
          <h3 :id="`${maskID}-title`" class="mt-2 font-semibold text-slate-900">
            {{ unavailable ? 'This step is unavailable' : current.title }}
          </h3>
          <p :id="`${maskID}-description`" class="mt-1 text-sm leading-5 text-slate-600">
            {{
              unavailable
                ? 'The expected control is not available. Exit the tour and try again when the page is ready.'
                : current.text
            }}
          </p>
        </div>
        <p class="mt-2 text-[11px] text-slate-400">{{ definition.previewNotice }}</p>
        <button
          v-if="returnLabel"
          type="button"
          class="mt-2 text-sm font-medium text-purple-700 underline"
          @click="stop(true, true)"
        >
          {{ returnLabel }}
        </button>
        <div class="mt-3 h-1 overflow-hidden rounded bg-slate-100">
          <div class="h-full bg-purple-500" :style="{ width: `${((step + progress) / steps.length) * 100}%` }" />
        </div>
        <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div class="flex gap-1">
            <Button
              v-if="!reducedMotion && !finished && !unavailable"
              type="button"
              variant="ghost"
              size="sm"
              @click="dispatch({ kind: 'togglePlay' })"
              >{{ playing ? 'Pause' : 'Play' }}</Button
            ><Button type="button" variant="ghost" size="sm" @click="dispatch({ kind: 'replay' })">Replay</Button>
          </div>
          <div class="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              :disabled="step === 0 || preparing"
              @click="dispatch({ kind: 'goTo', step: step - 1 })"
              >Back</Button
            ><Button
              v-if="step < steps.length - 1"
              type="button"
              size="sm"
              :disabled="preparing || unavailable"
              @click="dispatch({ kind: 'goTo', step: step + 1 })"
              >Next</Button
            ><Button v-else type="button" size="sm" @click="stop(false, true)">Finish tour</Button>
          </div>
        </div>
      </section>
    </div>
  </Teleport>
</template>
